import React, { useEffect, useState } from "react";
import { enqueueSnackbar } from "notistack";
import { Modal3 } from "../../DIsplay/Modal/Modal3";
import TextInput from "../../form/TextInput";
import Button1 from "../../form/Button1";
import denyChangeTicketRequest from "../../../controllers/booking/postTicketing/change/denyChangeTicketRequest";
import declineChangeBySupplier from "../../../controllers/booking/postTicketing/change/declineChangeBySupplier";
import {
  getChangeDeclineActionLabel,
  getChangeKindLabel,
  isChangeCloseRequestStage,
  shouldChooseChangeDeclineSource,
} from "../../../data/flight/order/getChangeAdminGuidance";
import StaffApiErrorModal, {
  getStaffApiErrorCopy,
} from "../StaffApiErrorModal";

const SOURCE = {
  airline: "airline",
  team: "team",
};

function defaultAirlineReason(data) {
  const kind = getChangeKindLabel(data) || "change";
  return `Airline declined the ${kind}`;
}

function notifySupplierDeclineSuccess(res) {
  const warning = res?.warning;
  const code = warning?.code ?? warning?.errorCode ?? warning;
  if (code === 827 || code === "827") {
    enqueueSnackbar(
      "Airline decline recorded, but returning the agent payment failed. Record the decline again to retry the return.",
      { variant: "warning" },
    );
    return;
  }
  enqueueSnackbar("Recorded as airline / supplier declined.", {
    variant: "success",
  });
}

/**
 * Decline / close a date change, details change, or open ticket.
 * Airline/supplier → POST declineBySupplier (reason required).
 * Team / Ops → PATCH deny (rejectReason).
 */
export default function ChangeDeclineModal({ open, setOpen, data, onDone }) {
  const [source, setSource] = useState("");
  const [rejectReason, setRejectReason] = useState("");
  const [supplierResponseCode, setSupplierResponseCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [apiError, setApiError] = useState(null);

  const order = typeof data === "object" && data ? data : null;
  const closing = isChangeCloseRequestStage(order);
  const needsChoice = shouldChooseChangeDeclineSource(order);
  const kind = getChangeKindLabel(order);
  const airlineDefault = defaultAirlineReason(order);

  useEffect(() => {
    if (!open) {
      setSource("");
      setRejectReason("");
      setSupplierResponseCode("");
      setLoading(false);
      return;
    }
    setSource("");
    setRejectReason(order?.rejectReason || "");
    setSupplierResponseCode("");
  }, [open, order?._id]);

  function close() {
    if (loading) return;
    setOpen?.(false);
  }

  async function handleConfirm() {
    if (!order?._id) return;

    if (needsChoice) {
      if (!source) {
        enqueueSnackbar(
          "Choose whether the airline or your team is declining.",
          { variant: "warning" },
        );
        return;
      }

      if (source === SOURCE.airline) {
        const reason = (rejectReason.trim() || airlineDefault).trim();
        if (!reason) {
          enqueueSnackbar("Say why the airline declined.", {
            variant: "warning",
          });
          return;
        }
        setLoading(true);
        const body = { reason };
        if (supplierResponseCode.trim()) {
          body.supplierResponseCode = supplierResponseCode.trim();
        }
        const res = await declineChangeBySupplier(order._id, body).finally(
          () => setLoading(false),
        );
        if (res.return) {
          notifySupplierDeclineSuccess(res);
          setOpen?.(false);
          onDone?.(res.data);
        } else {
          setApiError(getStaffApiErrorCopy(res));
        }
        return;
      }

      const reason = rejectReason.trim();
      if (!reason) {
        enqueueSnackbar(`Say why your team is declining this ${kind}.`, {
          variant: "warning",
        });
        return;
      }
      setLoading(true);
      const res = await denyChangeTicketRequest(order._id, {
        rejectReason: reason,
      }).finally(() => setLoading(false));
      if (res.return) {
        enqueueSnackbar("Request has been declined.", { variant: "success" });
        setOpen?.(false);
        onDone?.(res.data);
      } else {
        setApiError(getStaffApiErrorCopy(res));
      }
      return;
    }

    const reason = rejectReason.trim();
    if (!reason) {
      enqueueSnackbar(
        closing
          ? "Say why you are closing this request."
          : `Say why you are declining this ${kind}.`,
        { variant: "warning" },
      );
      return;
    }
    setLoading(true);
    const res = await denyChangeTicketRequest(order._id, {
      rejectReason: reason,
    }).finally(() => setLoading(false));
    if (res.return) {
      enqueueSnackbar(
        closing
          ? "This request has been closed."
          : "Request has been declined.",
        { variant: "success" },
      );
      setOpen?.(false);
      onDone?.(res.data);
    } else {
      setApiError(getStaffApiErrorCopy(res));
    }
  }

  if (!open) return null;

  const title = closing
    ? `Close ${kind} request`
    : `Decline ${kind} request`;

  return (
    <>
      <StaffApiErrorModal
        open={Boolean(apiError)}
        setOpen={(next) => {
          if (!next) setApiError(null);
        }}
        title={apiError?.title}
        text={apiError?.text}
      />
      <Modal3 open={open} setOpen={setOpen} title={title}>
        <div className="card p-4 flex flex-col gap-4 min-w-[300px] max-w-[440px]">
          {needsChoice ? (
            <>
              <p className="text-sm text-gray-600">
                Who is declining this {kind}? Airline / supplier and Ops use
                different endpoints and end statuses.
              </p>
              <div className="flex flex-col gap-2">
                <label
                  className={`flex items-start gap-3 rounded-xl border px-3 py-2.5 cursor-pointer ${
                    source === SOURCE.airline
                      ? "border-primary1 bg-primary1/5"
                      : "border-gray-200 hover:bg-gray-50"
                  }`}
                >
                  <input
                    type="radio"
                    name="change-decline-source"
                    className="mt-1"
                    checked={source === SOURCE.airline}
                    onChange={() => {
                      setSource(SOURCE.airline);
                      if (!rejectReason.trim()) {
                        setRejectReason(airlineDefault);
                      }
                    }}
                    disabled={loading}
                  />
                  <span>
                    <span className="block text-sm font-semibold text-gray-900">
                      Airline / supplier declined
                    </span>
                    <span className="block text-xs text-gray-500 mt-0.5">
                      Ends as declined by supplier. Original ticket unchanged.
                    </span>
                  </span>
                </label>
                <label
                  className={`flex items-start gap-3 rounded-xl border px-3 py-2.5 cursor-pointer ${
                    source === SOURCE.team
                      ? "border-primary1 bg-primary1/5"
                      : "border-gray-200 hover:bg-gray-50"
                  }`}
                >
                  <input
                    type="radio"
                    name="change-decline-source"
                    className="mt-1"
                    checked={source === SOURCE.team}
                    onChange={() => {
                      setSource(SOURCE.team);
                      if (rejectReason === airlineDefault) {
                        setRejectReason("");
                      }
                    }}
                    disabled={loading}
                  />
                  <span>
                    <span className="block text-sm font-semibold text-gray-900">
                      Declined by team
                    </span>
                    <span className="block text-xs text-gray-500 mt-0.5">
                      Ops decision — PATCH deny.
                    </span>
                  </span>
                </label>
              </div>
              {source === SOURCE.airline ? (
                <div className="flex flex-col gap-3">
                  <TextInput
                    label="Reason"
                    multiline
                    rows={3}
                    placeholder={airlineDefault}
                    value={rejectReason}
                    onChange={(ev) => setRejectReason(ev.target.value)}
                    disabled={loading}
                  />
                  <TextInput
                    label="Supplier response code (optional)"
                    placeholder="e.g. airline / GDS code"
                    value={supplierResponseCode}
                    onChange={(ev) => setSupplierResponseCode(ev.target.value)}
                    disabled={loading}
                  />
                </div>
              ) : null}
              {source === SOURCE.team ? (
                <TextInput
                  label=""
                  multiline
                  rows={4}
                  placeholder="e.g. Duplicate request, fare not available, Ops decision"
                  value={rejectReason}
                  onChange={(ev) => setRejectReason(ev.target.value)}
                  disabled={loading}
                />
              ) : null}
            </>
          ) : (
            <>
              <h5 className="text-sm font-medium text-gray-900">
                {closing
                  ? "Why are you closing this request?"
                  : "Why are you declining this request?"}
              </h5>
              <TextInput
                label=""
                multiline
                rows={5}
                placeholder="e.g. Fare not available, passenger already travelled, duplicate request"
                value={rejectReason}
                onChange={(ev) => setRejectReason(ev.target.value)}
                disabled={loading}
              />
            </>
          )}

          <div className="flex justify-end items-center gap-3">
            <Button1
              variant="outlined"
              className="btn-outlined flex-1"
              onClick={close}
              disabled={loading}
            >
              Cancel
            </Button1>
            <div>
              <Button1 onClick={handleConfirm} loading={loading}>
                {source === SOURCE.airline
                  ? "Record airline decline"
                  : getChangeDeclineActionLabel(order)}
              </Button1>
            </div>
          </div>
        </div>
      </Modal3>
    </>
  );
}
